import moment from 'moment';

/**
 * Comprehensive validation utilities for Enhanced SMART Goals
 */

/**
 * Validate enhanced SMART goal data with multi-goal structure
 * @param {Object} goalData - SMART goal data to validate
 * @returns {Object} - Validation result with isValid and errors
 */
export const validateSmartGoalData = (goalData) => {
  const errors = [];

  // Required field validations
  if (!goalData.problem) {
    errors.push('Problem selection is required');
  }

  if (!goalData.system) {
    errors.push('Organ system classification is required');
  }

  if (!goalData.mdSmartGoalOrder || !goalData.mdSmartGoalOrder.trim()) {
    errors.push('MD SMART Goal Order is required');
  } else if (goalData.mdSmartGoalOrder.length > 500) {
    errors.push('MD SMART Goal Order cannot exceed 500 characters');
  }

  // Validate patient goals array
  if (!goalData.patientGoals || !Array.isArray(goalData.patientGoals) || goalData.patientGoals.length === 0) {
    errors.push('At least one patient goal is required');
  } else {
    // Validate each patient goal
    goalData.patientGoals.forEach((patientGoal, index) => {
      const goalErrors = validatePatientGoalData(patientGoal, index + 1);
      errors.push(...goalErrors);
    });
  }
  
  // Timeline validations
  if (goalData.timeline) {
    if (!goalData.timeline.targetDate) {
      errors.push('Target completion date is required');
    } else {
      const targetDate = moment(goalData.timeline.targetDate);
      const startDate = goalData.timeline.startDate ? moment(goalData.timeline.startDate) : moment();
      
      if (!targetDate.isValid()) {
        errors.push('Invalid target date format');
      } else if (targetDate.isBefore(startDate)) {
        errors.push('Target date must be after start date');
      }
    }
    
    if (goalData.timeline.nextReviewDate) {
      const reviewDate = moment(goalData.timeline.nextReviewDate);
      if (!reviewDate.isValid()) {
        errors.push('Invalid review date format');
      } else if (reviewDate.isBefore(moment())) {
        errors.push('Review date cannot be in the past');
      }
    }
  }
  
  // Note: Actions are now validated within individual patient goals
  
  // Optional field length validations
  if (goalData.clientFeedback && goalData.clientFeedback.length > 1000) {
    errors.push('Client feedback cannot exceed 1000 characters');
  }
  
  if (goalData.hmReviewNote && goalData.hmReviewNote.length > 1000) {
    errors.push('Health manager notes cannot exceed 1000 characters');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Validate individual patient goal data
 * @param {Object} patientGoal - Patient goal data to validate
 * @param {number} goalIndex - Index of the goal for error messages
 * @returns {Array} - Array of error messages
 */
export const validatePatientGoalData = (patientGoal, goalIndex = 1) => {
  const errors = [];

  // Patient goal text validation
  if (!patientGoal.smartGoal || !patientGoal.smartGoal.trim()) {
    errors.push(`Patient Goal ${goalIndex}: SMART goal text is required`);
  } else if (patientGoal.smartGoal.length > 1000) {
    errors.push(`Patient Goal ${goalIndex}: SMART goal text cannot exceed 1000 characters`);
  }

  // Related pillar validation (single pillar per patient goal)
  if (!patientGoal.relatedPillar) {
    errors.push(`Patient Goal ${goalIndex}: Related pillar is required`);
  } else if (!['Medication', 'Nutrition', 'Movement', 'Recovery', 'Emotion'].includes(patientGoal.relatedPillar)) {
    errors.push(`Patient Goal ${goalIndex}: Invalid related pillar`);
  }

  // Tier validation
  if (!patientGoal.tier || ![1, 2, 3, 4].includes(parseInt(patientGoal.tier))) {
    errors.push(`Patient Goal ${goalIndex}: Valid tier (1-4) is required`);
  }

  // Timeline validation - all timeline fields are required
  if (!patientGoal.timeline) {
    errors.push(`Patient Goal ${goalIndex}: Timeline is required`);
  } else {
    // Start date validation
    if (!patientGoal.timeline.startDate) {
      errors.push(`Patient Goal ${goalIndex}: Start date is required`);
    } else {
      const startDate = moment(patientGoal.timeline.startDate);
      if (!startDate.isValid()) {
        errors.push(`Patient Goal ${goalIndex}: Invalid start date format`);
      }
    }

    // Target date validation  
    if (!patientGoal.timeline.targetDate) {
      errors.push(`Patient Goal ${goalIndex}: Target date is required`);
    } else {
      const targetDate = moment(patientGoal.timeline.targetDate);
      if (!targetDate.isValid()) {
        errors.push(`Patient Goal ${goalIndex}: Invalid target date format`);
      } else if (patientGoal.timeline.startDate) {
        const startDate = moment(patientGoal.timeline.startDate);
        if (startDate.isValid() && targetDate.isSameOrBefore(startDate)) {
          errors.push(`Patient Goal ${goalIndex}: Target date must be after start date`);
        }
      }
    }

    // Review date validation
    if (!patientGoal.timeline.nextReviewDate) {
      errors.push(`Patient Goal ${goalIndex}: Next review date is required`);
    } else {
      const reviewDate = moment(patientGoal.timeline.nextReviewDate);
      if (!reviewDate.isValid()) {
        errors.push(`Patient Goal ${goalIndex}: Invalid review date format`);
      }
    }
  }

  // Actions validation for this patient goal
  if (patientGoal.actions && Array.isArray(patientGoal.actions)) {
    patientGoal.actions.forEach((action, actionIndex) => {
      if (!action.description || !action.description.trim()) {
        errors.push(`Patient Goal ${goalIndex}, Action ${actionIndex + 1}: Description is required`);
      } else if (action.description.length > 200) {
        errors.push(`Patient Goal ${goalIndex}, Action ${actionIndex + 1}: Description cannot exceed 200 characters`);
      }

      if (action.notes && action.notes.length > 500) {
        errors.push(`Patient Goal ${goalIndex}, Action ${actionIndex + 1}: Notes cannot exceed 500 characters`);
      }
    });
  }

  return errors;
};

/**
 * Validate action data
 * @param {Object} actionData - Action data to validate
 * @returns {Object} - Validation result
 */
export const validateActionData = (actionData) => {
  const errors = [];
  
  if (!actionData.description || !actionData.description.trim()) {
    errors.push('Action description is required');
  } else if (actionData.description.length > 200) {
    errors.push('Action description cannot exceed 200 characters');
  }
  
  if (actionData.notes && actionData.notes.length > 500) {
    errors.push('Action notes cannot exceed 500 characters');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Validate education material data
 * @param {Object} materialData - Education material data to validate
 * @returns {Object} - Validation result
 */
export const validateEducationMaterialData = (materialData) => {
  const errors = [];
  
  if (!materialData.type || !['file', 'link'].includes(materialData.type)) {
    errors.push('Material type must be either "file" or "link"');
  }
  
  if (!materialData.title || !materialData.title.trim()) {
    errors.push('Material title is required');
  } else if (materialData.title.length > 200) {
    errors.push('Material title cannot exceed 200 characters');
  }
  
  if (materialData.type === 'link') {
    if (!materialData.url || !materialData.url.trim()) {
      errors.push('URL is required for link type materials');
    } else {
      // Basic URL validation
      try {
        new URL(materialData.url);
      } catch {
        errors.push('Invalid URL format');
      }
    }
  } else if (materialData.type === 'file') {
    if (!materialData.fileId) {
      errors.push('File ID is required for file type materials');
    }
  }
  
  if (materialData.description && materialData.description.length > 500) {
    errors.push('Material description cannot exceed 500 characters');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Validate review scheduling data
 * @param {Object} reviewData - Review data to validate
 * @returns {Object} - Validation result
 */
export const validateReviewData = (reviewData) => {
  const errors = [];
  
  if (!reviewData.nextReviewDate) {
    errors.push('Next review date is required');
  } else {
    const reviewDate = moment(reviewData.nextReviewDate);
    if (!reviewDate.isValid()) {
      errors.push('Invalid review date format');
    } else if (reviewDate.isBefore(moment().startOf('day'))) {
      errors.push('Review date cannot be in the past');
    }
  }
  
  if (reviewData.reviewNotes && reviewData.reviewNotes.length > 1000) {
    errors.push('Review notes cannot exceed 1000 characters');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Validate file upload data
 * @param {File} file - File to validate
 * @returns {Object} - Validation result
 */
export const validateFileUpload = (file) => {
  const errors = [];
  const maxSize = 10 * 1024 * 1024; // 10MB
  const allowedTypes = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'image/jpeg',
    'image/png',
    'image/gif',
    'text/plain'
  ];
  
  if (!file) {
    errors.push('File is required');
    return { isValid: false, errors };
  }
  
  if (file.size > maxSize) {
    errors.push('File size cannot exceed 10MB');
  }
  
  if (!allowedTypes.includes(file.type)) {
    errors.push('File type not supported. Please upload PDF, Word documents, images, or text files');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Validate completion rate calculation
 * @param {Array} actions - Array of actions
 * @returns {Object} - Validation result with calculated rate
 */
export const validateAndCalculateCompletionRate = (actions) => {
  const errors = [];
  
  if (!Array.isArray(actions)) {
    errors.push('Actions must be an array');
    return { isValid: false, errors, completionRate: 0 };
  }
  
  if (actions.length === 0) {
    return { isValid: true, errors: [], completionRate: 0 };
  }
  
  let completedCount = 0;
  actions.forEach((action, index) => {
    if (typeof action.completed !== 'boolean') {
      errors.push(`Action ${index + 1}: Completed status must be boolean`);
    } else if (action.completed) {
      completedCount++;
    }
  });
  
  const completionRate = Math.round((completedCount / actions.length) * 100);
  
  return {
    isValid: errors.length === 0,
    errors,
    completionRate
  };
};

/**
 * Sanitize text input to prevent XSS
 * @param {string} text - Text to sanitize
 * @returns {string} - Sanitized text
 */
export const sanitizeTextInput = (text) => {
  if (typeof text !== 'string') return '';
  
  return text
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;')
    .trim();
};

/**
 * Validate organ system against allowed values
 * @param {string} system - Organ system to validate
 * @returns {boolean} - Whether system is valid
 */
export const validateOrganSystem = (system) => {
  const allowedSystems = [
    'neurological',
    'cardiovascular', 
    'respiratory',
    'metabolic',
    'filtration',
    'musculoskeletal',
    'immune'
  ];
  
  return allowedSystems.includes(system);
};

/**
 * Validate pillar against allowed values (single pillar for patient goals)
 * @param {string} pillar - Pillar to validate
 * @returns {Object} - Validation result
 */
export const validatePillar = (pillar) => {
  const errors = [];
  const allowedPillars = ['Medication', 'Nutrition', 'Movement', 'Recovery', 'Emotion'];

  if (!pillar) {
    errors.push('Pillar is required');
  } else if (!allowedPillars.includes(pillar)) {
    errors.push(`Invalid pillar: ${pillar}`);
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Validate multiple patient goals structure
 * @param {Array} patientGoals - Array of patient goals to validate
 * @returns {Object} - Validation result
 */
export const validateMultiplePatientGoals = (patientGoals) => {
  const errors = [];

  if (!Array.isArray(patientGoals)) {
    errors.push('Patient goals must be an array');
    return { isValid: false, errors };
  }

  if (patientGoals.length === 0) {
    errors.push('At least one patient goal is required');
    return { isValid: false, errors };
  }

  // Validate each patient goal
  patientGoals.forEach((goal, index) => {
    const goalErrors = validatePatientGoalData(goal, index + 1);
    errors.push(...goalErrors);
  });

  // Check for duplicate pillars (optional validation)
  const pillars = patientGoals.map(goal => goal.relatedPillar).filter(Boolean);
  const uniquePillars = [...new Set(pillars)];
  if (pillars.length !== uniquePillars.length) {
    // This is just a warning, not an error
    console.warn('Multiple patient goals use the same pillar');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Calculate overall completion rate from multiple patient goals
 * @param {Array} patientGoals - Array of patient goals
 * @returns {Object} - Calculation result with overall rate
 */
export const calculateOverallCompletionRate = (patientGoals) => {
  const errors = [];

  if (!Array.isArray(patientGoals) || patientGoals.length === 0) {
    return { isValid: true, overallCompletionRate: 0, errors: [] };
  }

  let totalRate = 0;
  let validGoals = 0;

  patientGoals.forEach((goal, index) => {
    if (goal.actions && Array.isArray(goal.actions)) {
      const completedActions = goal.actions.filter(action => action.completed).length;
      const totalActions = goal.actions.length;
      const rate = totalActions > 0 ? Math.round((completedActions / totalActions) * 100) : 0;

      totalRate += rate;
      validGoals++;
    } else {
      errors.push(`Patient Goal ${index + 1}: Invalid actions array`);
    }
  });

  const overallCompletionRate = validGoals > 0 ? Math.round(totalRate / validGoals) : 0;

  return {
    isValid: errors.length === 0,
    overallCompletionRate,
    errors
  };
};
